import {
  LoanOfficer,
  LeadRecord,
  OpportunityRecord,
  LeadSource,
  FunnelStep,
} from '../types/throughput'
import { subDays, format, addDays } from 'date-fns'

// Constants
const LEAD_SOURCES: LeadSource[] = [
  'Web',
  'Referral',
  'Partner',
  'Phone',
  'Email',
  'Other',
]
const FUNNEL_STEPS: FunnelStep[] = [
  'Created',
  'Converted',
  'Disclosures Sent',
  'Disclosures Received',
  'Submitted to UW',
  'Approved with Conditions',
  'Closing Scheduled',
  'Funded',
]

// Mock Users
export const MOCK_OFFICERS: LoanOfficer[] = [
  { id: 'svp-1', name: 'Sarah Sterling', role: 'SVP', isActive: true },
  {
    id: 'tl-1',
    name: 'Mike Ross',
    role: 'Team Lead',
    isActive: true,
    svpId: 'svp-1',
  },
  {
    id: 'tl-2',
    name: 'Jessica Pearson',
    role: 'Team Lead',
    isActive: true,
    svpId: 'svp-1',
  },
  {
    id: 'lo-1',
    name: 'Rachel Zane',
    role: 'Loan Officer',
    isActive: true,
    svpId: 'svp-1',
    teamLeadId: 'tl-1',
  },
  {
    id: 'lo-2',
    name: 'Donna Paulsen',
    role: 'Loan Officer',
    isActive: true,
    svpId: 'svp-1',
    teamLeadId: 'tl-1',
  },
  {
    id: 'lo-3',
    name: 'Louis Litt',
    role: 'Loan Officer',
    isActive: true,
    svpId: 'svp-1',
    teamLeadId: 'tl-2',
  },
  {
    id: 'lo-4',
    name: 'Harvey Specter',
    role: 'Loan Officer',
    isActive: true,
    svpId: 'svp-1',
    teamLeadId: 'tl-2',
  },
  {
    id: 'lo-5',
    name: 'Alex Williams',
    role: 'Loan Officer',
    isActive: false,
    svpId: 'svp-1',
    teamLeadId: 'tl-1',
  },
]

// Generators
const getRandomItem = <T,>(arr: T[]): T =>
  arr[Math.floor(Math.random() * arr.length)]
const getRandomDate = (start: Date, end: Date) => {
  return new Date(
    start.getTime() + Math.random() * (end.getTime() - start.getTime()),
  )
}

export const generateMockData = () => {
  const leads: LeadRecord[] = []
  const opportunities: OpportunityRecord[] = []

  const startDate = subDays(new Date(), 90)
  const endDate = new Date()

  // Generate 500 leads
  for (let i = 0; i < 500; i++) {
    const owner = getRandomItem(
      MOCK_OFFICERS.filter((o) => o.role === 'Loan Officer'),
    )
    const createdDate = getRandomDate(startDate, endDate)
    const isConverted = Math.random() > 0.4 // 60% conversion rate

    const lead: LeadRecord = {
      id: `lead-${i}`,
      name: `Lead ${i}`,
      email: `lead${i}@example.com`,
      phone: `555-01${i.toString().padStart(2, '0')}`,
      source: getRandomItem(LEAD_SOURCES),
      createdAt: createdDate.toISOString(),
      ownerId: owner.id,
      isConverted,
    }

    if (isConverted) {
      const convertedDate = addDays(
        createdDate,
        Math.floor(Math.random() * 5) + 1,
      )
      lead.convertedDate = convertedDate.toISOString()
      lead.opportunityId = `opp-${i}`

      // Determine how far the opportunity goes
      const funnelDepth = Math.random()
      let currentStage: FunnelStep = 'Converted'

      const opp: OpportunityRecord = {
        id: `opp-${i}`,
        name: `${lead.name} Opportunity`,
        amount: Math.floor(Math.random() * 500000) + 200000,
        stage: 'Converted',
        ownerId: owner.id,
        leadId: lead.id,
        createdDate: createdDate.toISOString(),
        convertedDate: convertedDate.toISOString(),
      }

      // Simulate funnel progression
      if (funnelDepth > 0.2) {
        opp.stage = 'Disclosures Sent'
        opp.disclosuresSentDate = addDays(convertedDate, 2).toISOString()
      }
      if (funnelDepth > 0.3) {
        opp.stage = 'Disclosures Received'
        opp.disclosuresReceivedDate = addDays(convertedDate, 5).toISOString()
      }
      if (funnelDepth > 0.45) {
        opp.stage = 'Submitted to UW'
        opp.submittedToUWDate = addDays(convertedDate, 8).toISOString()
      }
      if (funnelDepth > 0.6) {
        opp.stage = 'Approved with Conditions'
        opp.approvedWithConditionsDate = addDays(
          convertedDate,
          12,
        ).toISOString()
      }
      if (funnelDepth > 0.75) {
        opp.stage = 'Closing Scheduled'
        opp.closingScheduledDate = addDays(convertedDate, 20).toISOString()
      }
      if (funnelDepth > 0.85) {
        opp.stage = 'Funded'
        opp.fundedDate = addDays(convertedDate, 30).toISOString()
      }

      opportunities.push(opp)
    }

    leads.push(lead)
  }

  return { leads, opportunities, officers: MOCK_OFFICERS }
}
